import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { SearchFilters } from '../../types';

interface SearchFiltersPanelProps {
  filters: SearchFilters;
  onFiltersChange: (filters: SearchFilters) => void;
  onClose: () => void;
}

export const SearchFiltersPanel: React.FC<SearchFiltersPanelProps> = ({
  filters,
  onFiltersChange,
  onClose
}) => {
  const categories = [
    'Fashion & Beauty',
    'Travel & Lifestyle',
    'Food & Cooking',
    'Fitness & Health',
    'Technology',
    'Gaming',
    'Music',
    'Art & Design',
    'Business',
    'Education',
    'Entertainment',
    'Sports',
    'Parenting',
    'Pets'
  ];

  const followerRanges = [
    { label: 'Nano (1K-10K)', value: 1000 },
    { label: 'Micro (10K-100K)', value: 10000 },
    { label: 'Mid-tier (100K-1M)', value: 100000 },
    { label: 'Macro (1M-10M)', value: 1000000 },
    { label: 'Mega (10M+)', value: 10000000 }
  ];

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-500"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Category Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category
          </label>
          <select
            value={filters.category || ''}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        {/* Location Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Location
          </label>
          <input
            type="text"
            placeholder="Enter location"
            value={filters.location || ''}
            onChange={(e) => handleFilterChange('location', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        {/* Follower Count Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Minimum Followers
          </label>
          <select
            value={filters.minFollowers || ''}
            onChange={(e) => handleFilterChange('minFollowers', e.target.value ? Number(e.target.value) : undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          >
            <option value="">Any</option>
            {followerRanges.map((range) => (
              <option key={range.value} value={range.value}>
                {range.label}
              </option>
            ))}
          </select>
        </div>

        {/* Rating Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Minimum Rating
          </label>
          <select
            value={filters.minRating || ''}
            onChange={(e) => handleFilterChange('minRating', e.target.value ? Number(e.target.value) : undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          >
            <option value="">Any Rating</option>
            <option value="4.5">4.5+ Stars</option>
            <option value="4.0">4.0+ Stars</option>
            <option value="3.5">3.5+ Stars</option>
            <option value="3.0">3.0+ Stars</option>
          </select>
        </div>
      </div>

      {/* Advanced Filters */}
      <div className="border-t pt-6">
        <h4 className="text-sm font-medium text-gray-900 mb-4">Advanced Filters</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Engagement Rate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Engagement Rate (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              placeholder="e.g., 3.5"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          {/* Rate per Post */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maximum Rate per Post ($)
            </label>
            <input
              type="number"
              min="0"
              step="100"
              placeholder="e.g., 5000"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
        </div>
      </div>

      {/* Platform Filters */}
      <div className="border-t pt-6">
        <h4 className="text-sm font-medium text-gray-900 mb-4">Platforms</h4>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { name: 'Instagram', icon: '📸' },
            { name: 'YouTube', icon: '🎥' },
            { name: 'TikTok', icon: '🎵' },
            { name: 'Twitter', icon: '🐦' }
          ].map((platform) => (
            <label key={platform.name} className="flex items-center space-x-3">
              <input
                type="checkbox"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">
                {platform.icon} {platform.name}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Verification Status */}
      <div className="border-t pt-6">
        <h4 className="text-sm font-medium text-gray-900 mb-4">Status</h4>

        <div className="space-y-3">
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Verified influencers only</span>
          </label>

          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Available now</span>
          </label>
        </div>
      </div>

      {/* Apply Filters Button */}
      <div className="border-t pt-6">
        <div className="flex justify-between items-center">
          <button
            onClick={() => {
              onFiltersChange({
                category: '',
                location: '',
                minFollowers: undefined,
                minRating: undefined,
                limit: 12
              });
            }}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Reset All Filters
          </button>

          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};
